import { useCallback, useEffect, useState, ChangeEvent } from 'react';
import { useSelector } from 'react-redux';
import _template from 'lodash/template';
import debounce from 'lodash/debounce';
import moment from 'moment';
import { getAllUsers } from '@services/userService';
import { getElementComponent } from './helpers';
import { Field } from './Field';
import { cleanHtmlValue } from '@utils';
import _get from 'lodash/get';
import { currentSubmissionIdSelector } from '@selectors/submission';

export const FieldContainer = (props) => {
  const { schema, form, user, handleChange, lang } = props;
  const [autocompleteOptions, setOptions] = useState([]);
  const [autocompleteValue, setValue] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const { type, wrapperStyle, label, noDefault } = schema;
  const submissionId = useSelector(currentSubmissionIdSelector);
  const [inputValue, setInputValue] = useState(form);
  const [checked, setChecked] = useState(schema.value);
  const [isUsersLoading, setLoading] = useState(false);

  useEffect(() => {
    if (submissionId && !noDefault) {
      setSelectedDate(moment());
    }
  }, [submissionId]);

  useEffect(() => {
    if (_get(form, schema.key) && noDefault) {
      const formatDate =
        typeof form[schema.key] === 'string'
          ? moment(form[schema.key]).endOf('day')
          : form[schema.key].endOf('day');

      setSelectedDate(formatDate);
    }
  }, [form]);

  useEffect(() => {
    setInputValue(form);
  }, [form]);

  const handleOnChange = useCallback(async (value) => {
    setValue(value);
    setLoading(true);
    const { items } = await getAllUsers({ fullName: value, limit: 6 }, lang);

    setOptions(items);
    setLoading(false);
  }, []);

  const debouncedHandleOnChange = useCallback(debounce(handleOnChange, 300), [handleOnChange]);

  const handleGroupChange = useCallback(
    (key, value) => {
      if (!form[schema.key]) {
        handleChange({ [key]: cleanHtmlValue(value) });
      } else {
        handleChange({
          ...form[schema.key],
          [key]: cleanHtmlValue(value),
        });
      }
    },
    [form, handleChange, schema.key]
  );

  const handleDateChange = useCallback(
    (date) => {
      setSelectedDate(date);
      handleChange(date);
    },
    [handleChange]
  );

  const handleChangeInput = useCallback(
    (event) => {
      const value = cleanHtmlValue(event.currentTarget.value);
      const newInputValue = inputValue;

      newInputValue[schema.key] = value;
      schema.value = value;

      setInputValue(newInputValue);
      handleChange(value);
    },
    [handleChange, inputValue, schema.key, schema.value]
  );

  const handleChangeCheckbox = useCallback(
    (event) => {
      const value = event.currentTarget.checked;

      setChecked(value);
      handleChange(value);
    },
    [handleChange]
  );

  const element = getElementComponent({
    schema,
    handleChange,
    autocompleteOptions,
    autocompleteValue,
    handleOnChange: (event: ChangeEvent<HTMLInputElement>) =>
      debouncedHandleOnChange(cleanHtmlValue(event.target.value)),
    form,
    handleGroupChange,
    handleChangeInput,
    inputValue,
    selectedDate,
    handleDateChange,
    handleChangeCheckbox,
    checked,
    type,
    isUsersLoading,
    lang,
  });

  const interpolated = _template(label)({ user });

  return <Field wrapperStyle={wrapperStyle} interpolated={interpolated} element={element} />;
};
